import { createHash, createHmac } from 'crypto';

// 腾讯云 API 签名 v3（TC3-HMAC-SHA256）实现
// 本代码基于腾讯云API签名v3文档实现: https://cloud.tencent.com/document/product/213/30654
// 请严格按照文档说明使用，不建议随意修改签名相关代码

function sha256(message: string, secret: string | Buffer): Buffer {
    return createHmac('sha256', secret).update(message).digest();
}

function sha256Hex(message: string, secret: string | Buffer): string {
    return createHmac('sha256', secret).update(message).digest('hex');
}

function getHash(message: string): string {
    return createHash('sha256').update(message).digest('hex');
}

function getDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const year = date.getUTCFullYear();
    const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
    const day = ('0' + date.getUTCDate()).slice(-2);
    return `${year}-${month}-${day}`;
}

export type TencentSignParams = {
    /** 密钥 SecretId */
    secretId: string;
    /** 密钥 SecretKey */
    secretKey: string;
    /** 请求域名，如 vod.tencentcloudapi.com */
    host: string;
    /** 服务名，如 vod */
    service: string;
    /** 接口名，如 CreateAigcVideoTask */
    action: string;
    /** 接口版本，如 2018-07-17 */
    version: string;
    /** 请求体（必须与实际发送的字符串完全一致） */
    payload: string;
    /** 地域（可选） */
    region?: string;
    /** 临时密钥 Token（可选） */
    token?: string;
};

/**
 * 生成携带腾讯云 v3 签名的请求头（POST + application/json）
 */
export function buildTencentCloudHeaders(params: TencentSignParams): Record<string, string> {
    const { secretId, secretKey, host, service, action, version, payload, region, token } = params;

    const timestamp = Math.floor(Date.now() / 1000);
    const date = getDate(timestamp);

    // ************* 步骤 1：拼接规范请求串 *************
    const signedHeaders = 'content-type;host';
    const hashedRequestPayload = getHash(payload);
    const httpRequestMethod = 'POST';
    const canonicalUri = '/';
    const canonicalQueryString = '';
    const canonicalHeaders =
        'content-type:application/json; charset=utf-8\n' + 'host:' + host + '\n';

    const canonicalRequest =
        httpRequestMethod +
        '\n' +
        canonicalUri +
        '\n' +
        canonicalQueryString +
        '\n' +
        canonicalHeaders +
        '\n' +
        signedHeaders +
        '\n' +
        hashedRequestPayload;

    // ************* 步骤 2：拼接待签名字符串 *************
    const algorithm = 'TC3-HMAC-SHA256';
    const hashedCanonicalRequest = getHash(canonicalRequest);
    const credentialScope = date + '/' + service + '/' + 'tc3_request';
    const stringToSign =
        algorithm +
        '\n' +
        timestamp +
        '\n' +
        credentialScope +
        '\n' +
        hashedCanonicalRequest;

    // ************* 步骤 3：计算签名 *************
    const kDate = sha256(date, 'TC3' + secretKey);
    const kService = sha256(service, kDate);
    const kSigning = sha256('tc3_request', kService);
    const signature = sha256Hex(stringToSign, kSigning);

    // ************* 步骤 4：拼接 Authorization *************
    const authorization =
        algorithm +
        ' ' +
        'Credential=' +
        secretId +
        '/' +
        credentialScope +
        ', ' +
        'SignedHeaders=' +
        signedHeaders +
        ', ' +
        'Signature=' +
        signature;

    // ************* 步骤 5：构造请求头 *************
    const headers: Record<string, string> = {
        Authorization: authorization,
        'Content-Type': 'application/json; charset=utf-8',
        Host: host,
        'X-TC-Action': action,
        'X-TC-Timestamp': String(timestamp),
        'X-TC-Version': version,
    };

    if (region) {
        headers['X-TC-Region'] = region;
    }
    if (token) {
        headers['X-TC-Token'] = token;
    }

    return headers;
}
